import { ImageName } from "@mcleod/images";
import { ClickListener, DataHeader, DataSource, DataSourceMode, Image, Label, Layout, Panel } from "@mcleod/components";
import { Api, HorizontalAlignment, ModelRow, UrlUtil } from "@mcleod/core";
import { AutogenLayoutMovementTabView } from "./autogen/AutogenLayoutMovementTabView";
import { ManifestMovementQuickInfo } from "./ManifestMovementQuickInfo";
import { Movement } from "./Movement";
import { MovementTabs } from "./MovementTabs";

export class MovementTabView extends AutogenLayoutMovementTabView {
    private movementCache = null;
    private movementTabs: MovementTabs;
    private tabChangeEvent: ClickListener = () => this.handleOrderTabChange();
    private invoiceHistoryTabChangeEvent: ClickListener = () => this.handleInvoiceHistoryTabChange();
    public showVoid: boolean = false;
    public syncTitle = () => {};
    public activeMovementChanged = (movement: Movement) => {};
    public handleOrderTabChange = () => {};
    public handleInvoiceHistoryTabChange = () => {};
    public movementTabOnChange = () => {};
    public movementRefreshed = () => {};

    override get mainDataSource(): DataSource {
        return this.layoutMovement.mainDataSource;
    }

    override set mainDataSource(value: DataSource) {
        super.mainDataSource = value;
    }

    override get activeRow(): ModelRow {
        return this.layoutMovement.activeRow;
    }

    override get dataHeader(): DataHeader {
        return this.layoutMovement.dataHeader;
    }

    onLoad(): void | Promise<void> {
        this.movementTabs = this.layoutMovementTabs;
        this.layoutMovement.movementTabView = this;
        this.layoutMovement.panelOrderTab.addClickListener(this.tabChangeEvent);
        this.layoutMovement.panelInvoiceHistoryTab.addClickListener(this.invoiceHistoryTabChangeEvent);
    }

    private getActiveLayout(id: string): Movement {
        let cachedLayout = this.movementCache[id];
        if (!cachedLayout) {
            cachedLayout = Layout.getLayout(this.layoutMovement.layoutName);
            cachedLayout.movementTabView = this;
            this.movementCache[id] = cachedLayout;
        }
        return cachedLayout;
    }

    setActiveMovement(id: string) {
        if (this.movementCache == null) {
            this.movementCache = {};
            this.movementCache[id] = this.layoutMovement;
        }
        const activeMovement = this.getActiveLayout(id);
        activeMovement.addLayoutLoadListener(async () => {
            this.activeMovementChanged(activeMovement);
            if (this.movementTabs.currentTabId == null) {
                await this.addTabs(true);
            }
            if (!activeMovement.activeRow) {
                await activeMovement.sourceMovement.search({ id });
            }
            if (this.layoutMovement != activeMovement) {
                activeMovement.dataHeader.showSave = activeMovement.dataHeader.showSaveAndClose = activeMovement.dataHeader.showClose =
                this.layoutMovement.dataHeader.showSave = this.layoutMovement.dataHeader.showSaveAndClose = this.layoutMovement.dataHeader.showClose;
                activeMovement.dataHeader.navigateOnClose = this.layoutMovement.dataHeader.navigateOnClose;
                this.replace(this.layoutMovement, activeMovement);
                this.replaceListener(this.layoutMovement, activeMovement);
                this.layoutMovement = activeMovement;
                this.layoutMovement.mainDataSource.mode = DataSourceMode.UPDATE;
                //Call to sync data header including ellipses actions
                this.layoutMovement.dataHeader.syncTitle(DataSourceMode.UPDATE);
                this.movementTabOnChange();
            }
            activeMovement.panelMovementTabController.add(this.movementTabs);
            this.movementTabs.visible = true;
            this.setSplitImage();
        });
    }

    setSplitImage()  {
        const movementTab = this.movementTabs.panelMovementTabInfo.findComponentById(this.movementTabs.currentTabId) as Panel;
        const currentTabImage = movementTab?.findComponentById("imageYSplit") as Image;
        if (currentTabImage != null) {
            this.layoutMovement.imageYSplit.setProps({
                name: currentTabImage.name,
                color: currentTabImage.color,
                visible: true
            });
        }
        else{
            this.layoutMovement.imageYSplit.visible = false;
        }
    }
    public getChangedMovement(): Movement {
        for (const m in this.movementCache) {
            if (this.movementCache[m].mainDataSource.hasChanged()) {
                return this.movementCache[m];
            }
        }
        return null;
    }

    async refreshData(moveId?: string) {
        await this.layoutMovement.search({ id: moveId != null ? moveId : this.layoutMovement.activeRow.get("id") });
        this.layoutMovement.dataHeader.syncTitle(DataSourceMode.UPDATE);
        if (this.movementTabs.movements.length === 0) {
            this.layoutMovement.imageYSplit.visible = false;
        }
        this.movementRefreshed();
    }

    private replaceListener(oldMove: Movement, newMove: Movement) {
        oldMove.panelOrderTab.removeClickListener(this.tabChangeEvent);
        oldMove.panelInvoiceHistoryTab.removeClickListener(this.invoiceHistoryTabChangeEvent);
        newMove.panelOrderTab.addClickListener(this.tabChangeEvent);
        newMove.panelInvoiceHistoryTab.addClickListener(this.invoiceHistoryTabChangeEvent);
    }

    /* Tab Controller */
    async addTabs(initialSetup: boolean) {
        this.movementTabs.panelMovementTabInfo.removeAll();
        const order_id = this.layoutMovement.activeRow?.get("movement_order.order_id");
        const manifest_id = this.layoutMovement.activeRow?.get("movement_order.planned_manifest_id");
        const movementUrl = manifest_id ? "lme/dispatch/manifest-movements" : "lme/dispatch/order-movements";
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        this.showVoid = urlParams?.showVoid ? urlParams.showVoid : this.showVoid;
        const inputParam = manifest_id ? { "manifest_id": manifest_id, "order_id": this.getOrderIdForManiftestMovement(order_id) } :
            this.layoutMovement.activeRow && order_id == null ? {"empty_movement": this.layoutMovement.activeRow.get("id")} : { "order_id": order_id };            
        const response = await Api.post(movementUrl, { ...inputParam, "brokerage_only": false,
            ...(this.showVoid && { "show_voided": "true" })});
        if (response.data.length === 1 && response.data[0].brokerage === "Y") {
            this.layoutMovement.panelOverview.borderTopWidth = 1;
            this.layoutMovement.panelOverview.borderTopColor = "subtle.light";
        }
        else if (response.data.length > 0) {
            const movements = response.data.map(move => new ModelRow(this.layoutMovement.sourceMovement.url, false, move));
            movements.sort((a: ModelRow, b: ModelRow) => {
                if (!a.isNull("manifest_id") && !b.isNull("manifest_id"))
                    return a.getDateTime("manifest_estimated_arrival")?.getTime() - b.getDateTime("manifest_estimated_arrival")?.getTime();
            });
            const urlId = urlParams?.key;
            const isMovement = movements.some(movement => movement.get("id") === urlId);
            if (this.movementTabs.selectedMovementId == null) {
                this.movementTabs.selectedMovementId = isMovement ? urlId : this.layoutMovement.activeRow.get("orders.curr_movement_id", "");
            }
            if (initialSetup) {
                this.movementTabs.movementTabOnClickCallback = (movement, newTab, oldTab) => this.movementTabOnClick(movement, newTab, oldTab);
                this.movementTabs.movementTabOnAdd = (movement) => this.movementTabOnAdd(movement);
            }
            this.movementTabs.addMovementTabs(movements);
        }
    }

    private movementTabOnAdd(movement: ModelRow) {
        const movementTab = this.movementTabs.getMovementTab(movement);
        let brokerageToCheck = "";
        //Added the logic below due to movements brokerage flags not being updated in the response
        //of the order-movement API call because of the way the it grabs movements from cache
        if (this.mainDataSource.activeRow.get("id") === movement.get("id") &&
            movement.get("brokerage", "N") !== this.mainDataSource.activeRow.get("brokerage", "N")) {
                brokerageToCheck = this.mainDataSource.activeRow.get("brokerage", "N");
        }
        else {
            brokerageToCheck = movement.get("brokerage", "N");
        }

        const showMovementTopper = brokerageToCheck === "N";
        if (showMovementTopper) {
            this.movementTabs.manageMovementToppers(movement.get("loaded") == "L", movementTab);
        }
        if (!movement.isNull("pending_payee_id")|| !movement.isNull("pending_carrier_nbr")) {
            this.addPendingLabelToTab(movementTab.tabInfo, movement);
        }
        if (!movement.isNull("manifest_id")) {
            this.addManifestImageToTab(movementTab.tabInfo, movement);
        }
    }

    addManifestImageToTab(tab: Panel, movement: ModelRow) {
        const imageManifest = new Image({ id: "imageManifest", name: ImageName.manifest, marginLeft: 8 });
        const manifestId = movement?.get("manifest_id");
        const movementId = movement?.get("id");
        ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(imageManifest, movementId, manifestId);
        tab.add(imageManifest);
    }

    createPendingLabel() {
        const pendingLabel = new Label({
            id: "pendingLabel", text: "Pending", backgroundColor: "warning", color: "warning.reverse",
            borderWidth: 1, borderRadius: 18, marginLeft: 10, marginRight: 10, fontSize: "small",
            paddingLeft: 20, paddingRight: 20, width: 110, fontBold: true, rowBreak: false
        });
        pendingLabel.align = HorizontalAlignment.CENTER;
        return pendingLabel;
    }

    addPendingLabelToTab(tab: Panel, movement: ModelRow) {
        const newPendingLabel = this.createPendingLabel();
        this.layoutMovement.setTooltipPendingLabelCallback(newPendingLabel, movement);
        tab.add(newPendingLabel);
    }

    async updateMovementTab() {
        const movementTab = this.movementTabs.getMovementTab(this.activeRow);
        const isPending = this.activeRow.get("has_pending_assignment", false);
        const pendingLabel = movementTab.tabInfo.findComponentById("pendingLabel") as Label;
        if (!pendingLabel && isPending) {
            this.addPendingLabelToTab(movementTab.tabInfo, this.activeRow);
        } else if (pendingLabel && !isPending) {
            movementTab.tabInfo.remove(pendingLabel);
        }
        const statusIcon = this.movementTabs.getTabComp(movementTab, "imageStatus") as Image;
        this.movementTabs.setStatusIcon(statusIcon, this.activeRow.get("status"), this.activeRow.get("loaded"), this.activeRow.getBoolean("is_locate_move"));
    }

    async movementTabOnClick(movement: ModelRow, newTab: Panel, oldTab: Panel) {
        // do we need this? history.replaceState({}, null, `?mode=update&key=${movement.get("id")}&orderId=${movement.get("order_id")}`);
        this.setActiveMovement(movement.get("id"));
        this.supressPendingBadgeTooltip(newTab, oldTab);
    }

    private supressPendingBadgeTooltip(newTab: Panel, oldTab: Panel) {
        const newTabPendingLabel = newTab.findComponentById("pendingLabel");
        const oldTabPendingLabel = oldTab.findComponentById("pendingLabel");
        if (newTabPendingLabel)
            newTabPendingLabel.allowTooltip = true;
        if (oldTabPendingLabel)
            oldTabPendingLabel.allowTooltip = false;
    }

    private getOrderIdForManiftestMovement(currOrderId: string): string {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        const urlId = urlParams?.key;
        let orderId = currOrderId;
        if (this.layoutMovement.mainDataSource?.rowCount > 0) {
            const row: ModelRow[] = this.layoutMovement.mainDataSource.data.filter((row: ModelRow) =>
                { return row.get("movement_order.order_id") === urlId });
            if (row != null && row.length > 0)
                orderId = urlId;
        }
        return orderId;
    }


}
